import React, { useState, useEffect } from "react";
// Force rebuild - production deployment
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  ListGroup,
  Alert,
  Table,
  Spinner,
  Form,
  Button,
  Modal,
  ToggleButton,
} from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import api from "../api";

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Bulk delete state
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [messageType, setMessageType] = useState("info");

  // System settings state
  const [enableRegistration, setEnableRegistration] = useState(true);
  const [enableGoogleLogin, setEnableGoogleLogin] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState(null);
  const [settingsMessageType, setSettingsMessageType] = useState("info");

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#8dd1e1",
    "#a4de6c",
    "#d0ed57",
  ];

  useEffect(() => {
    fetchStatistics();
    fetchSubjects();
    fetchSystemSettings();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/api/admin/statistics/");
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching admin statistics:", err);
      setError("Failed to load dashboard data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Add function to fetch system settings
  const fetchSystemSettings = async () => {
    try {
      const response = await api.get("/api/system-settings/");

      // Update state based on settings
      if (response.data.enable_registration !== undefined) {
        setEnableRegistration(response.data.enable_registration === "true");
      }

      if (response.data.enable_google_login !== undefined) {
        setEnableGoogleLogin(response.data.enable_google_login === "true");
      }

      console.log("System settings loaded:", response.data);
    } catch (err) {
      console.error("Error fetching system settings:", err);
      // If settings can't be fetched, we'll rely on the default values (true)
    }
  };

  // Add function to save system settings
  const saveSystemSetting = async (key, value) => {
    try {
      setSavingSettings(true);
      setSettingsMessage(null);

      const response = await api.post("/api/system-settings/", {
        key: key,
        value: String(value),
        description:
          key === "enable_registration"
            ? "Controls whether new user registration is allowed"
            : "Controls whether Google login is enabled",
      });

      setSettingsMessage(`Successfully updated ${key} setting`);
      setSettingsMessageType("success");
      console.log(`Setting ${key} updated:`, response.data);

      // Update the local state to reflect the new setting
      if (key === "enable_registration") {
        setEnableRegistration(value);
      } else if (key === "enable_google_login") {
        setEnableGoogleLogin(value);
      }
    } catch (err) {
      console.error(`Error saving ${key} setting:`, err);
      setSettingsMessage(`Failed to update ${key} setting. Please try again.`);
      setSettingsMessageType("danger");
    } finally {
      setSavingSettings(false);

      // Clear message after 3 seconds
      setTimeout(() => {
        setSettingsMessage(null);
      }, 3000);
    }
  };

  // Toggle handlers for settings
  const handleRegistrationToggle = () => {
    saveSystemSetting("enable_registration", !enableRegistration);
  };

  const handleGoogleLoginToggle = () => {
    saveSystemSetting("enable_google_login", !enableGoogleLogin);
  };

  // Add function to fetch subjects for bulk delete
  const fetchSubjects = async () => {
    try {
      const response = await api.get("/api/csv-data/subjects/");
      if (response.data && Array.isArray(response.data)) {
        setSubjects(response.data.filter(Boolean).sort());
      }
    } catch (err) {
      console.error("Error fetching subjects:", err);
    }
  };

  // Format date strings for charts
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Process data for user trends chart
  const processUserTrendData = () => {
    if (!stats?.user_metrics?.user_trends) return [];

    return stats.user_metrics.user_trends.map((item) => ({
      date: formatDate(item.day),
      users: item.count,
    }));
  };

  // Process data for worksheet trends chart
  const processWorksheetTrendData = () => {
    if (!stats?.worksheet_metrics?.worksheet_trends) return [];

    return stats.worksheet_metrics.worksheet_trends.map((item) => ({
      date: formatDate(item.day),
      worksheets: item.count,
    }));
  };

  // Process data for subject distribution pie chart
  const processSubjectData = () => {
    if (!stats?.content_metrics?.subjects_distribution) return [];

    return stats.content_metrics.subjects_distribution.map((item, index) => ({
      name: item.subject || "Unknown",
      value: item.count,
      fill: COLORS[index % COLORS.length],
    }));
  };

  // Process data for worksheet public/private pie chart
  const processWorksheetTypeData = () => {
    if (!stats?.worksheet_metrics) return [];

    return [
      {
        name: "Public",
        value: stats.worksheet_metrics.public_worksheets,
        fill: "#00C49F",
      },
      {
        name: "Private",
        value: stats.worksheet_metrics.private_worksheets,
        fill: "#0088FE",
      },
    ];
  };

  // Handle bulk delete confirmation
  const handleBulkDeleteClick = () => {
    if (!selectedSubject) {
      setDeleteMessage(
        "Please select a subject first before attempting to delete."
      );
      setMessageType("danger");
      return;
    }
    setConfirmationInput(""); // Reset confirmation input
    setShowDeleteConfirmModal(true);
  };

  // Handle confirmation input change
  const handleConfirmationInputChange = (e) => {
    setConfirmationInput(e.target.value);
  };

  // Execute bulk delete
  const executeBulkDelete = async () => {
    if (!selectedSubject || confirmationInput !== selectedSubject) return;

    try {
      setDeleting(true);

      // Send request to delete all records for the subject
      console.log(`Executing bulk delete for subject: ${selectedSubject}`);

      // Use the standalone bulk delete endpoint
      const response = await api.post("/api/bulk-delete/", {
        subject: selectedSubject,
      });
      console.log("Bulk delete response:", response.data);

      // Close the modal
      setShowDeleteConfirmModal(false);

      // Show success message
      setDeleteMessage(
        `Successfully deleted all ${selectedSubject} questions.`
      );
      setMessageType("success");

      // Reset selected subject
      setSelectedSubject("");

      // Refresh the statistics and subjects list
      fetchStatistics();
      fetchSubjects();
    } catch (err) {
      console.error("Error deleting records:", err);
      let errorMessage = `Failed to delete ${selectedSubject} records.`;

      if (err.response && err.response.data) {
        errorMessage += ` ${
          err.response.data.error ||
          err.response.data.detail ||
          JSON.stringify(err.response.data)
        }`;
      } else {
        errorMessage += " Please try again later.";
      }

      setDeleteMessage(errorMessage);
      setMessageType("danger");
    } finally {
      setDeleting(false);
    }
  };

  // Add a function to handle subject data download
  const handleDownloadSubject = async () => {
    if (!selectedSubject) {
      setDeleteMessage("Please select a subject to download.");
      setMessageType("danger");
      return;
    }

    try {
      setDeleteMessage(`Downloading data for ${selectedSubject}...`);
      setMessageType("info");

      // Create request headers with proper authorization
      const headers = {
        Accept: "*/*", // More permissive Accept header
      };

      // Manually add Authorization header if available
      const tokenData = localStorage.getItem("accessToken");
      if (tokenData) {
        try {
          // Try parsing as JSON first
          const parsed = JSON.parse(tokenData);
          if (parsed.token) {
            headers["Authorization"] = `Bearer ${parsed.token}`;
          }
        } catch (e) {
          // If not valid JSON, it might be a raw token
          if (typeof tokenData === "string") {
            headers["Authorization"] = `Bearer ${tokenData}`;
          }
        }
      }

      // Use api instance with our manually created headers
      const response = await api.get(
        `/api/export-subject/?subject=${encodeURIComponent(selectedSubject)}`,
        {
          responseType: "blob",
          headers: headers,
        }
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${selectedSubject}_data.csv`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      setDeleteMessage(`Download complete for ${selectedSubject}.`);
      setMessageType("success");

      // Clear the message after 3 seconds
      setTimeout(() => {
        setDeleteMessage(null);
      }, 3000);
    } catch (error) {
      console.error("Error downloading data:", error);

      // More detailed error logging
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);

        // Try to extract any text error message
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            console.error("Response data:", text);
          } catch (blobError) {
            console.error("Could not read blob data", blobError);
          }
        }
      }

      setDeleteMessage(`Error downloading data: ${error.message}`);
      setMessageType("danger");
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status" />
        <p className="mt-3">Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-3">
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert variant="warning" className="my-3">
        No dashboard data available.
      </Alert>
    );
  }

  return (
    <Container fluid>
      <h2 className="mb-4">Admin Dashboard</h2>

      {/* User Metrics Row */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <h1 className="display-4">
                {stats?.user_metrics?.total_users || 0}
              </h1>
              <Card.Title>Total Users</Card.Title>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <h1 className="display-4">
                {stats?.user_metrics?.new_users_month || 0}
              </h1>
              <Card.Title>New Users (30 Days)</Card.Title>
              <div className="small text-muted mt-2">
                Today: {stats?.user_metrics?.new_users_today || 0} | Week:{" "}
                {stats?.user_metrics?.new_users_week || 0}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <h1 className="display-4">
                {stats?.user_metrics?.active_sessions || 0}
              </h1>
              <Card.Title>Active Sessions</Card.Title>
              <Badge bg="success" className="mt-2">
                Online Now
              </Badge>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body className="text-center">
              <h1 className="display-4">
                {stats?.content_metrics?.pending_questions || 0}
              </h1>
              <Card.Title>Pending Questions</Card.Title>
              <Badge
                bg={
                  (stats?.content_metrics?.pending_questions || 0) > 0
                    ? "warning"
                    : "success"
                }
                className="mt-2"
              >
                {(stats?.content_metrics?.pending_questions || 0) > 0
                  ? "Needs Review"
                  : "All Reviewed"}
              </Badge>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* System Settings Row */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <strong>System Settings</strong>
            </Card.Header>
            <Card.Body>
              {settingsMessage && (
                <Alert
                  variant={settingsMessageType}
                  onClose={() => setSettingsMessage(null)}
                  dismissible
                >
                  {settingsMessage}
                </Alert>
              )}

              <h5>User Registration & Login Settings</h5>
              <p className="text-muted">
                Control user registration and login options
              </p>

              <Row className="align-items-center mb-3">
                <Col md={6}>
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <Form.Check
                        type="switch"
                        id="registration-switch"
                        checked={enableRegistration}
                        onChange={handleRegistrationToggle}
                        disabled={savingSettings}
                      />
                    </div>
                    <div>
                      <strong>Enable User Registration</strong>
                      <p className="text-muted mb-0 small">
                        When disabled, the Register button will be hidden and
                        new users cannot register
                      </p>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <Form.Check
                        type="switch"
                        id="google-login-switch"
                        checked={enableGoogleLogin}
                        onChange={handleGoogleLoginToggle}
                        disabled={savingSettings}
                      />
                    </div>
                    <div>
                      <strong>Enable Google Login</strong>
                      <p className="text-muted mb-0 small">
                        When disabled, the Google Login button will be hidden
                        and users cannot login with Google
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Content Metrics Row */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>User Registration Trend (30 Days)</Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={processUserTrendData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="users" name="New Users" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>Worksheet Creation Trend (30 Days)</Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={processWorksheetTrendData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="worksheets"
                    name="Worksheets Created"
                    fill="#82ca9d"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Question Details Row */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>Subject Distribution</Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div style={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          dataKey="value"
                          data={processSubjectData()}
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={false}
                        >
                          {processSubjectData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [value, "Questions"]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mt-3">
                    <h5>Questions by Subject</h5>
                    <ListGroup variant="flush">
                      {stats?.content_metrics?.questions_by_subject
                        ?.slice(0, 5)
                        .map((subject, idx) => (
                          <ListGroup.Item
                            key={idx}
                            className="d-flex justify-content-between align-items-center"
                          >
                            {subject.subject || "Unknown"}
                            <Badge bg="primary" pill>
                              {subject.count}
                            </Badge>
                          </ListGroup.Item>
                        ))}
                    </ListGroup>
                  </div>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className="text-muted">
              Total Questions: {stats?.content_metrics?.total_questions || 0}
            </Card.Footer>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>Worksheet Metrics</Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div style={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          dataKey="value"
                          data={processWorksheetTypeData()}
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                          }
                          labelLine={false}
                        >
                          {processWorksheetTypeData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [value, "Worksheets"]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mt-3">
                    <h5>Popular Subjects</h5>
                    <ListGroup variant="flush">
                      {stats?.worksheet_metrics?.popular_subjects
                        ?.slice(0, 5)
                        .map((subject, idx) => (
                          <ListGroup.Item
                            key={idx}
                            className="d-flex justify-content-between align-items-center"
                          >
                            {subject.subject || "Unknown"}
                            <Badge bg="info" pill>
                              {subject.count}
                            </Badge>
                          </ListGroup.Item>
                        ))}
                    </ListGroup>
                  </div>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className="text-muted">
              Total Worksheets:{" "}
              {stats?.worksheet_metrics?.total_worksheets || 0}
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Additional Metrics Row */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>User Engagement</Card.Header>
            <Card.Body>
              <h5>Most Active Users</h5>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Worksheets Created</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.engagement_metrics?.most_active_users?.map(
                    (user, idx) => (
                      <tr key={idx}>
                        <td>{user.user__username}</td>
                        <td>{user.worksheet_count}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>System Health</Card.Header>
            <Card.Body>
              <Table bordered>
                <tbody>
                  <tr>
                    <td>
                      <strong>Server Status</strong>
                    </td>
                    <td>
                      <Badge bg="success">
                        {stats?.system_health?.server_status || "Unknown"}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Uptime</strong>
                    </td>
                    <td>{stats?.system_health?.uptime || "Unknown"}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Average Response Time</strong>
                    </td>
                    <td>
                      {stats?.system_health?.avg_response_time || "Unknown"}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Database Size</strong>
                    </td>
                    <td>{stats?.system_health?.database_size || "Unknown"}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Bulk Delete Section */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header className="bg-danger text-white">
              <strong>Database Management</strong>
            </Card.Header>
            <Card.Body>
              {deleteMessage && (
                <Alert
                  variant={messageType}
                  onClose={() => setDeleteMessage(null)}
                  dismissible
                >
                  {deleteMessage}
                </Alert>
              )}

              <h5>Bulk Delete Questions</h5>
              <p className="text-muted">
                Delete all questions for a specific subject. This action cannot
                be undone.
              </p>

              <Row className="align-items-end">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Select Subject</Form.Label>
                    <Form.Select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                    >
                      <option value="">Select a Subject</option>
                      {subjects.map((subject, index) => (
                        <option key={index} value={subject}>
                          {subject}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="success"
                      className="me-2"
                      onClick={handleDownloadSubject}
                      disabled={!selectedSubject}
                    >
                      Download {selectedSubject} Data
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleBulkDeleteClick}
                      disabled={!selectedSubject}
                    >
                      Delete All {selectedSubject} Questions
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirmModal}
        onHide={() => setShowDeleteConfirmModal(false)}
        backdrop="static" // Prevent closing by clicking outside
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete <strong>ALL</strong> questions for
            the subject: <strong>{selectedSubject}</strong>?
          </p>
          <Alert variant="danger">
            <strong>Warning:</strong> This action cannot be undone. All
            questions for this subject will be permanently deleted from the
            database.
          </Alert>
          <Form.Group>
            <Form.Label>
              To confirm, type the subject name exactly:{" "}
              <strong>{selectedSubject}</strong>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder={`Type ${selectedSubject} to confirm`}
              value={confirmationInput}
              onChange={handleConfirmationInputChange}
            />
            {confirmationInput && confirmationInput !== selectedSubject && (
              <Form.Text className="text-danger">
                The text doesn't match. Please type the exact subject name.
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={executeBulkDelete}
            disabled={confirmationInput !== selectedSubject || deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Deleting...</span>
              </>
            ) : (
              "Delete All Questions"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;
